import React, { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import PlaneOrderOnPageModal from "@/components/analyzeOrderOnPage/PlanOrderOnPageModal"
import { PlanRouteTabBar } from "@/components/analyzeOrderOnPage/PlanRouteTabBar"
import {
  hasTabContent,
  PlanRouteSnapshot,
  tabLabel,
} from "@/components/analyzeOrderOnPage/planRouteTabs"
import type { PlanRouteTabsApi } from "@/components/analyzeOrderOnPage/usePlanRouteTabs"

type TabbedPlanOrderOnPageModalProps = Omit<
  React.ComponentProps<typeof PlaneOrderOnPageModal>,
  "sharedRouteState" | "tabBar" | "onStateChange" | "routeName"
> & {
  tabs: PlanRouteTabsApi
}

// The route planner with tabs above it. Mount it under tabs.plannerKey: it
// is mounted again for every tab switch and every time the planner opens,
// and starts from the active tab's latest snapshot.
export default function TabbedPlanOrderOnPageModal({
  tabs,
  ...modalProps
}: TabbedPlanOrderOnPageModalProps) {
  const { t } = useTranslation()
  // Frozen for this mount. The planner re-initialises whenever its
  // sharedRouteState changes, while the tab's snapshot changes on every edit.
  const [tabId] = useState(tabs.activeTab.id)
  const [startState] = useState(tabs.activeTab.snapshot ?? null)

  const { updateTabSnapshot, closeTab, restoreTab } = tabs

  const handleStateChange = useCallback(
    (snapshot: PlanRouteSnapshot) => updateTabSnapshot(tabId, snapshot),
    [tabId, updateTabSnapshot],
  )

  const handleCloseTab = useCallback(
    (id: string) => {
      const closed = closeTab(id)
      if (!closed || !hasTabContent(closed.tab)) return
      toast(t("planOrder.tabs.closed", { label: tabLabel(closed.tab, t) }), {
        action: {
          label: t("planOrder.tabs.undo"),
          onClick: () => restoreTab(closed),
        },
      })
    },
    [closeTab, restoreTab, t],
  )

  return (
    <PlaneOrderOnPageModal
      {...modalProps}
      sharedRouteState={startState}
      onStateChange={handleStateChange}
      routeName={tabs.activeTab.name}
      tabBar={
        <PlanRouteTabBar
          tabs={tabs.tabs}
          activeTabId={tabs.activeTab.id}
          canAdd={tabs.canAdd}
          onSelect={tabs.selectTab}
          onAdd={tabs.addTab}
          onClose={handleCloseTab}
          onRename={tabs.renameTab}
        />
      }
    />
  )
}
